import { fetchOverpassTurbo, isValidGeoJSON } from "senf-atomic-design-system";
import { v4 as uuidv4 } from "uuid";
import { geminiRequest } from "./geminiRequest";
import { searchGeoapify } from "./geoapify";
import { searchNominatim } from "./nominatim";

interface FetchMapboundsOptions {
  locationName: string;
  countryCode?: string;
  stateCode?: string;
  placeName?: string;
}

/**
 * Processes boundary features by splitting MultiPolygons and adding IDs
 * @param features - Array of GeoJSON features to process
 * @returns Processed FeatureCollection with split polygons and IDs
 */
const processMapboundFeatures = (features: any[]) => {
  // Split MultiPolygons into individual Polygons
  const splitFeatures = features.flatMap((feature) => {
    if (feature.geometry.type === "MultiPolygon") {
      // Convert each polygon in the MultiPolygon to a separate feature
      return feature.geometry.coordinates.map((coordinates) => ({
        type: "Feature",
        id: uuidv4(),
        properties: { ...feature.properties },
        geometry: {
          type: "Polygon",
          coordinates: coordinates,
        },
      }));
    }
    // For regular Polygons, just add an ID if not present
    return [
      {
        ...feature,
        id: feature.id || uuidv4(),
      },
    ];
  });

  const geoJson = {
    type: "FeatureCollection",
    features: splitFeatures,
  };

  // Validate the GeoJSON
  if (!isValidGeoJSON(JSON.stringify(geoJson))) {
    throw new Error("Invalid GeoJSON generated from features");
  }

  return geoJson;
};

/**
 * Fetches administrative boundaries for a location using multiple services
 * Priority order: Nominatim -> Geoapify -> Overpass Turbo with Gemini
 * @param options - Location search options
 * @returns Promise with GeoJSON features array
 * @throws Error if no boundaries are found
 */
export const fetchMapbounds = async (
  options: FetchMapboundsOptions,
): Promise<any[]> => {
  const { locationName, countryCode, stateCode, placeName } = options;

  console.log("Fetching boundaries for:", locationName);

  // Try Nominatim first
  console.log("Trying Nominatim...");
  const nominatimFeatures = await searchNominatim(locationName, {
    filterTypes: ["administrative", "city", "suburb", "postcode", "boundary"],
    countryCode,
    stateCode,
  });

  if (nominatimFeatures.length > 0) {
    // Sort by admin_level to get the most specific boundary
    const sortedFeatures = nominatimFeatures.sort((a, b) => {
      const aLevel = Number.parseInt(a.properties.admin_level || "0") || 0;
      const bLevel = Number.parseInt(b.properties.admin_level || "0") || 0;
      return bLevel - aLevel; // Higher admin_level means more specific
    });

    console.log("✓ Found boundaries via Nominatim");
    return [sortedFeatures[0]];
  }

  // Fallback to Geoapify
  console.log("No results from Nominatim, trying Geoapify...");
  const geoapifyFeatures = await searchGeoapify(locationName, {
    filterTypes: ["administrative", "city", "suburb", "postcode", "boundary"],
    countryCode,
    stateCode,
  });

  if (geoapifyFeatures.length > 0) {
    // Sort by admin_level to get the most specific boundary
    const sortedFeatures = geoapifyFeatures.sort((a, b) => {
      const aLevel = Number.parseInt(a.properties.admin_level || "0") || 0;
      const bLevel = Number.parseInt(b.properties.admin_level || "0") || 0;
      return bLevel - aLevel; // Higher admin_level means more specific
    });

    console.log("✓ Found boundaries via Geoapify");
    return [sortedFeatures[0]];
  }

  // Fallback to Overpass Turbo with Gemini
  console.log(
    "No results from Nominatim or Geoapify, trying Overpass Turbo with Gemini...",
  );

  const overPassCode = await geminiRequest({
    assistantId: "default",
    body: `
Generate a **fully correct Overpass Turbo query** that finds the **most specific** administrative boundary for a given place.


  **Inputs:**
- Place name: ${placeName || locationName}
- Country code: ${countryCode || ""}
- State code: ${stateCode || ""}

---
**🔹 STRICT RULES (FOLLOW EXACTLY):**
1. **Query ALL available administrative boundaries** for the given place:
   - Use: \`relation["boundary"="administrative"]["name"="..."]\`
   - Do **NOT** hardcode \`admin_level="6"\`, \`admin_level="8"\`, etc.
   - Retrieve **ALL** admin levels for the place.
2. **From the results, find the most specific boundary**:
   - The **most specific** admin level has the **highest number**.
   - Example: If İzmir has admin_level=4, 6, 8, 9', the query **must return admin_level=9**.
3. **Do NOT use any of these invalid filters:**
   - ❌ \`area[name="..."]\` (invalid for relations)
   - ❌ \`area.a\`, \`around.a\`, \`within\`
   - ❌ \`ISO3166-2\`
   - ❌ \`if:t["admin_level"]\` (Overpass does not support dynamic filtering)
   - ❌ \`order by t["admin_level"] desc\` (Overpass does not support sorting)
4. **Return the Overpass query in this format (NO EXTRA TEXT):**
   \`\`\`overpass
   (Overpass Turbo Query)
   \`\`\`

---
**Example Correct Queries:**

**For İzmir (Turkey, most specific admin level 9)**:
\`\`\`overpass
[out:json][timeout:25];
(
  relation["boundary"="administrative"]["name"="İzmir"]["admin_level"="9"];
);
out geom;
\`\`\`

**For Berlin (Germany, most specific admin level 10)**:
\`\`\`overpass
[out:json][timeout:25];
(
  relation["boundary"="administrative"]["name"="Berlin"]["admin_level"="10"];
);
out geom;
\`\`\`

**For New York City (USA, most specific admin level 8)**:
\`\`\`overpass
[out:json][timeout:25];
(
  relation["boundary"="administrative"]["name"="New York"]["admin_level"="8"];
);
out geom;
\`\`\`
`,
    lang: "en",
  });

  // Extract the Overpass query from the response
  const overpassQuery =
    overPassCode.match(/```overpass\n([\s\S]+?)\n```/)?.[1] || "";

  console.log("Generated Overpass query:", overpassQuery);

  const overPassFeatures = await fetchOverpassTurbo(overpassQuery);

  console.log("Overpass response:", overPassFeatures);
  console.log("Overpass features count:", overPassFeatures?.features?.length);

  if (overPassFeatures?.features && overPassFeatures.features.length > 0) {
    console.log("✓ Found boundaries via Overpass Turbo");
    return [overPassFeatures.features[0]];
  }

  // No results from any source
  console.warn(
    "No boundaries found - Nominatim, Geoapify, and Overpass all returned empty",
  );
  throw new Error("No boundaries found for this location");
};

/**
 * Fetches and processes mapbounds with callbacks for success and error handling
 * This is the main function to use in components
 * @param options - Location search options
 * @param onResolve - Callback function called with processed GeoJSON on success
 * @param onError - Callback function called with error on failure
 */
export const fetchAndProcessMapbounds = async (
  options: FetchMapboundsOptions,
  onResolve: (geoJson: any) => void,
  onError: (error: Error) => void,
) => {
  try {
    // Fetch the boundaries
    const features = await fetchMapbounds(options);

    // Process the features (split MultiPolygons, add IDs, validate)
    const processedGeoJson = processMapboundFeatures(features);

    // Call success callback
    onResolve(processedGeoJson);
  } catch (error) {
    // Call error callback
    onError(error as Error);
  }
};

/**
 * Fetches and processes mapbounds from a Geocoder result
 * Extracts country and state codes from the Geocoder context
 * @param result - Geocoder result object with text, place_name, and context
 * @param onResolve - Callback function called with processed GeoJSON on success
 * @param onError - Callback function called with error on failure
 */
export const fetchAndProcessMapboundsFromGeocoder = async (
  result: any,
  onResolve: (geoJson: any) => void,
  onError: (error: Error) => void,
) => {
  if (!result) {
    onError(new Error("No geocoder result provided"));
    return;
  }

  console.log("Searching for:", result);

  // Extract state and country from context
  const countryCode = result.context?.find((item) =>
    item.id?.includes("country"),
  )?.short_code;
  const stateCode = result.context?.find((item) =>
    item.id?.includes("region"),
  )?.short_code;

  console.log("Country:", countryCode);
  console.log("State:", stateCode);

  // Call the main function with extracted data
  await fetchAndProcessMapbounds(
    {
      locationName: result.text,
      countryCode,
      stateCode,
      placeName: result.place_name,
    },
    onResolve,
    onError,
  );
};
